import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  LocationTypeEnum,
  Query,
  WithdrawalLocation,
} from '@varo/graphql-schema-types';
import { setStringAsync } from 'expo-clipboard';
import { LocationObject } from 'expo-location';
import { Stack } from 'expo-router';
import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import GetAtmPoints from 'services/graphql/queries/GetAtmPoints';
import PredictionMapView from 'shared/components/maps/PredictionMapView/PredictionMapView';
import { PlaceDetailsResponse } from 'shared/components/maps/PredictionMapView/PredictionMapView.helpers';
import BottomSheet from 'shared/components/vxl/BottomSheet/BottomSheet';
import Button from 'shared/components/vxl/Button/Button';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { usePressableAnalytics } from 'shared/hooks/analytics/usePressableAnalytics';
import { GlobalUIContext } from 'shared/providers/globalUIProvider/GlobalUIContextProvider/GlobalUIContextProvider';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import openMarkerInMaps from 'shared/utils/maps/openMarkerInMaps';
import { useQuery } from 'urql';

function FindAtmScreen() {
  const { t: translatedText } = useTranslation('moveMoney', {
    keyPrefix: 'findAtm',
  });
  const [selectedMarker, setSelectedMarker] =
    useState<WithdrawalLocation | null>(null);
  const pointDetailsBottomSheetRef = useRef<BottomSheetModal>(null);
  const [coordinates, setCoordinates] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);
  const [{ data: atmPointsData, fetching: isAtmPointsFetching }] =
    useQuery<Query>({
      query: GetAtmPoints(LocationTypeEnum.Withdrawal),
      variables: coordinates ?? undefined,
      pause: !coordinates,
      requestPolicy: 'cache-and-network',
    });

  const { setSnackBarMessage } = useContext(GlobalUIContext);

  const selectedMarkerAddress = `${selectedMarker?.address?.addressLine1}, ${selectedMarker?.address?.city}, ${selectedMarker?.address?.state} ${selectedMarker?.address?.zip}`;

  const handlePredictionSelected = (placeDetails: PlaceDetailsResponse) => {
    setCoordinates({
      latitude: parseFloat(placeDetails?.result?.geometry?.location?.lat),
      longitude: parseFloat(placeDetails?.result?.geometry?.location?.lng),
    });
  };

  const handleOnLocationChange = useCallback(
    (
      location: LocationObject | google.maps.LatLngLiteral,
      isInitialLoad?: boolean,
    ) => {
      const latitude =
        'coords' in location ? location.coords.latitude : location.lat;
      const longitude =
        'coords' in location ? location.coords.longitude : location.lng;

      if (isInitialLoad) {
        setCoordinates({ latitude, longitude });
      }
    },
    [],
  );

  const pinSelectedEvent = usePressableAnalytics({
    analyticsId: 'pinClicked',
    customAttributes: {
      atm_location_name: selectedMarker?.name || '',
      atm_address:
        `${selectedMarker?.address?.addressLine1}, ${selectedMarker?.address?.city}, ${selectedMarker?.address?.state} ${selectedMarker?.address?.zip}` as string,
      atm_zip_code: selectedMarker?.address?.zip || '',
    },
  });

  const handleMarkerPress = (point: WithdrawalLocation) => {
    setSelectedMarker(point);
    pointDetailsBottomSheetRef.current?.present();
  };

  const handleBottomSheetDismiss = useCallback(() => {
    setSelectedMarker(null);
  }, []);

  const handleOpenInMaps = () => {
    if (selectedMarker) {
      openMarkerInMaps(selectedMarker);
    }
  };

  const copyToClipboard = (text?: string) => {
    if (text) {
      void setStringAsync(text);
      setSnackBarMessage?.(translatedText('copied'));
    }
    pointDetailsBottomSheetRef.current?.dismiss();
  };

  useEffect(() => {
    if (selectedMarker) {
      pinSelectedEvent();
    }
  }, [selectedMarker, pinSelectedEvent]);

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: false,
        }}
      />
      <PredictionMapView
        navigationBarTitle={translatedText('header')}
        onPredictionSelected={handlePredictionSelected}
        onMarkerPress={handleMarkerPress}
        isLoading={isAtmPointsFetching}
        onLocationChange={handleOnLocationChange}
        markerData={atmPointsData?.withdrawalLocations ?? []}
        requestLocationTitle={translatedText('requestLocationTitle')}
        requestLocationBody={translatedText('requestLocationBody')}
        selectedLocationId={selectedMarker?.id}
      />

      <BottomSheet
        ref={pointDetailsBottomSheetRef}
        onDismiss={handleBottomSheetDismiss}
      >
        <TitleMdText style={styles.title}>{selectedMarker?.name}</TitleMdText>
        <ListItem
          primaryCopy={selectedMarkerAddress}
          secondaryCopy={selectedMarker?.distance}
          showsChevron={false}
          trailingAccessory={{
            id: 'copyIcon',
            name: VXLComponentId.ICON,
            props: {
              iconKey: 'COPY',
            },
          }}
          onPress={() => copyToClipboard(selectedMarkerAddress)}
        />
        <View style={styles.buttonContainer}>
          <Button
            label={translatedText('openInMaps')}
            type="SECONDARY"
            onPress={handleOpenInMaps}
            analyticsId="openMaps"
            customAttributes={{
              atm_location_name: selectedMarker?.name || '',
              atm_address: selectedMarkerAddress,
              atm_zip_code: selectedMarker?.address?.zip || '',
            }}
          />
        </View>
      </BottomSheet>
    </>
  );
}

const styles = StyleSheet.create({
  buttonContainer: {
    marginTop: theme.spacing.m,
    padding: theme.spacing.m,
  },
  title: {
    paddingHorizontal: theme.spacing.m,
  },
});

export default FindAtmScreen;
